import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import PartCutUploadControl from "@/components/PartCutUploadControl";
import { formatUploadSize } from "@/components/PartCutUploadControl";
import { Archive, Check, Clock3, Film, LoaderCircle, Pencil, Plus, Scissors, Trash2, Unlink, Upload } from "lucide-react";

const PART_STATUSES = ["awaiting_upload", "processing", "editing", "ready", "published", "archived"];
const CUT_TYPES = ["full_movie", "trailer", "teaser", "of_cut", "jff_cut", "custom"];
const PLATFORMS = ["onlyfans", "jff", "x", "instagram", "tiktok", "youtube", "website", "other"];
const ACCESS_TYPES = ["free", "ppv_paid_unlock", "subscriber_only", "private"];

const statusIcons = {
  awaiting_upload: Upload,
  processing: LoaderCircle,
  editing: Scissors,
  ready: Check,
  published: Check,
  archived: Archive,
};

const pretty = value => (value || "unknown").replaceAll("_", " ");

const LEGACY_VARIANTS = [
  ["part1", "Part 1"], ["part1_trailer", "Part 1 Trailer"],
  ["part2", "Part 2"], ["part2_trailer", "Part 2 Trailer"],
  ["part3", "Part 3"], ["part3_trailer", "Part 3 Trailer"],
  ["onlyfans_cut", "OF Cut"], ["onlyfans_trailer", "OF Trailer"],
];

export default function VideoPartsPanel({ video, onUpdate, onOpenLegacyManager, sourceVideoOptions }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editor, setEditor] = useState(null);
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  const loadData = useCallback(async () => {
    try {
      const response = await api.get(`/api/videos/${video.id}/parts-cuts`);
      setData(response.data);
      onUpdateRef.current?.({ id: video.id, platform_status: response.data.master_video.platform_status });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to load parts and cuts");
    } finally {
      setLoading(false);
    }
  }, [video.id]);

  useEffect(() => {
    setLoading(true);
    loadData();
  }, [loadData]);

  const saveAsset = async (kind, values) => {
    try {
      if (kind === "part") {
        if (values.id) await api.patch(`/api/video-parts/${values.id}`, values.payload);
        else await api.post(`/api/videos/${video.id}/parts`, values.payload);
      } else if (values.id) {
        await api.patch(`/api/video-cuts/${values.id}`, values.payload);
      } else {
        await api.post(`/api/videos/${video.id}/cuts`, values.payload);
      }
      toast.success(values.id ? "Asset updated" : `${kind === "part" ? "Part" : "Cut"} added`);
      setEditor(null);
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save asset");
    }
  };

  const removePart = async part => {
    const linkedCount = data.cuts.filter(cut => cut.parent_id === part.id || cut.part_id === part.id).length;
    const prompt = linkedCount
      ? `Part ${part.part_number} has ${linkedCount} linked cut(s). Delete the part and those cuts?`
      : `Delete Part ${part.part_number}?`;
    if (!window.confirm(prompt)) return;
    try {
      await api.delete(`/api/video-parts/${part.id}`, { params: { cascade: linkedCount > 0 } });
      toast.success("Part deleted");
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete part");
    }
  };

  const removeCut = async cut => {
    if (!window.confirm(`Delete “${cut.label || pretty(cut.cut_type)}”?`)) return;
    try {
      await api.delete(`/api/video-cuts/${cut.id}`);
      toast.success("Cut deleted");
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete cut");
    }
  };

  const detachSourceVideo = async (kind, asset) => {
    if (!window.confirm(`Detach ${asset.source_video_id} from this ${kind}?`)) return;
    try {
      await api.patch(`/api/video-${kind === "part" ? "parts" : "cuts"}/${asset.id}`, { source_video_id: null });
      toast.success("Existing video detached; part/cut metadata retained");
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not detach video");
    }
  };

  const linkPlatform = async (platform, kind, assetId) => {
    try {
      await api.patch(`/api/videos/${video.id}/platform-status/${platform}`, { asset_ref: assetId ? { kind, asset_id: assetId } : null });
      toast.success("Platform linked");
      await loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not link platform");
    }
  };

  if (loading) return <div className="py-4 text-sm text-zinc-500">Loading parts and cuts...</div>;
  if (!data) return null;

  const { master_video: master, parts, cuts, badges } = data;
  const sourceVideoTitle = sourceId => sourceVideoOptions?.find(option => option.id === sourceId)?.title || sourceId;
  const matrix = master.platform_status || {};
  const legacyVariants = LEGACY_VARIANTS
    .map(([key, label]) => ({ key, label, pid: video?.variants?.[key]?.mux_playback_id || video?.variants?.[key]?.playback_id }))
    .filter(v => v.pid);
  const platformLinks = (kind, id) => (
    <PlatformLinks matrix={matrix} kind={kind} assetId={id} onLink={(platform, assetId) => linkPlatform(platform, kind, assetId)} />
  );
  return (
    <section className="space-y-3" aria-label="Parts and Cuts">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Film size={17} className="text-cyan-300" />
          <h3 className="text-sm font-semibold text-white">Parts &amp; Cuts</h3>
          <span className="text-xs text-zinc-500">{badges.parts} parts · {badges.cuts} cuts</span>
        </div>
        <div className="flex gap-2">
          {onOpenLegacyManager && (
            <button type="button" onClick={onOpenLegacyManager} data-testid="legacy-variants-btn" className="inline-flex items-center gap-1 rounded border border-zinc-700 px-2.5 py-1.5 text-xs text-zinc-400 hover:border-pink-500" title="Upload/manage legacy variant slots (Part 1-3, OF Cut, trailers)">
              <Upload size={14} /> Uploads
            </button>
          )}
          <button type="button" onClick={() => setEditor({ kind: "part" })} className="inline-flex items-center gap-1 rounded border border-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 hover:border-cyan-500">
            <Plus size={14} /> Part
          </button>
          <button type="button" onClick={() => setEditor({ kind: "cut" })} className="inline-flex items-center gap-1 rounded border border-zinc-700 px-2.5 py-1.5 text-xs text-zinc-200 hover:border-cyan-500">
            <Plus size={14} /> Cut
          </button>
        </div>
      </header>

      <AssetRow
        title="Full Movie"
        subtitle="Master video"
        status={master.mux_status || (master.playback_id ? "ready" : "master")}
        uploadId={master.mux_upload_id}
        assetId={master.mux_asset_id}
        playbackId={master.playback_id}
        duration={master.duration_seconds}
        posterUrl={master.poster_url}
        onEdit={() => setEditor({ kind: "cut", initial: { parent_type: "master", parent_id: video.id, cut_type: "full_movie", target_platform: "other", access_type: "free" } })}
      />

      {parts.map(part => (
        <AssetRow
          key={part.id}
          title={`Part ${part.part_number}${part.label ? ` · ${part.label}` : ""}`}
          subtitle={[part.description, part.source_video_id ? `Linked video: ${sourceVideoTitle(part.source_video_id)}` : "", part.notes].filter(Boolean).join(" · ")}
          status={part.status}
          uploadId={part.mux_upload_id}
          assetId={part.mux_asset_id}
          playbackId={part.playback_id}
          duration={part.duration_seconds}
          createdAt={part.upload_started_at || part.created_at}
          uploadState={part.upload_state}
          uploadFilename={part.upload_filename}
          uploadSizeBytes={part.upload_size_bytes}
          uploadError={part.upload_error}
          thumbnailEndpoint={`/api/video-parts/${part.id}/thumbnail.jpg`}
          posterUrl={sourceVideoOptions?.find(option => option.id === part.source_video_id)?.thumbnail_url}
          uploadControl={<PartCutUploadControl videoId={video.id} kind="part" asset={part} onRefresh={loadData} />}
          onEdit={() => setEditor({ kind: "part", initial: part })}
          onDelete={() => removePart(part)}
          onDetach={sourceVideoOptions && part.source_video_id ? () => detachSourceVideo("part", part) : undefined}
          extra={platformLinks("part", part.id)}
        />
      ))}

      {cuts.length > 0 && (
        <div className="space-y-1.5 border-t border-zinc-800 pt-3">
          <div className="flex items-center gap-2 text-[11px] font-semibold uppercase text-zinc-500">
            <Scissors size={13} /> Platform cuts
          </div>
          {cuts.map(cut => {
            const parent = cut.parent_type === "part" ? parts.find(part => part.id === cut.parent_id) : null;
            const parentLabel = cut.parent_type === "part" ? (parent ? `Part ${parent.part_number}` : "Unlinked part") : "Full Movie";
            return (
              <AssetRow
                key={cut.id}
                compact
                title={cut.label || pretty(cut.cut_type)}
                subtitle={`${pretty(cut.cut_type)} · ${cut.target_platform} · ${pretty(cut.access_type)}${cut.price != null ? ` · ${cut.currency || "USD"} ${cut.price}` : ""} · from ${parentLabel}${cut.description ? ` · ${cut.description}` : ""}${cut.source_video_id ? ` · linked video: ${sourceVideoTitle(cut.source_video_id)}` : ""}`}
                status={cut.platform_publish_status}
                uploadId={cut.mux_upload_id}
                assetId={cut.mux_asset_id}
                playbackId={cut.playback_id}
                duration={cut.duration_seconds}
                createdAt={cut.upload_started_at || cut.created_at}
                uploadState={cut.upload_state}
                uploadFilename={cut.upload_filename}
                uploadSizeBytes={cut.upload_size_bytes}
                uploadError={cut.upload_error}
                thumbnailEndpoint={`/api/video-cuts/${cut.id}/thumbnail.jpg`}
                posterUrl={sourceVideoOptions?.find(option => option.id === cut.source_video_id)?.thumbnail_url}
                uploadControl={<PartCutUploadControl videoId={video.id} kind="cut" asset={cut} onRefresh={loadData} />}
                onEdit={() => setEditor({ kind: "cut", initial: cut })}
                onDelete={() => removeCut(cut)}
                onDetach={sourceVideoOptions && cut.source_video_id ? () => detachSourceVideo("cut", cut) : undefined}
                extra={platformLinks("cut", cut.id)}
              />
            );
          })}
        </div>
      )}

      {legacyVariants.length > 0 && (
        <div className="space-y-1.5 border-t border-zinc-800 pt-3" data-testid="legacy-variants">
          <div className="text-[11px] font-semibold uppercase text-zinc-500">Legacy variants (video.variants)</div>
          <div className="flex flex-wrap gap-2">
            {legacyVariants.map(({ key, label, pid }) => (
              <span key={key} className="inline-flex items-center gap-1.5 rounded border border-pink-500/30 bg-zinc-900/60 px-2 py-1 text-xs text-pink-300" title={`Playback ${pid}`}>
                <img src={`https://image.mux.com/${pid}/thumbnail.jpg?width=64&height=36&fit_mode=smartcrop`} alt={label} className="h-5 w-9 rounded object-cover" loading="lazy" />
                {label} ✓
              </span>
            ))}
          </div>
        </div>
      )}

      {!parts.length && !cuts.length && !legacyVariants.length && (
        <p className="border-t border-zinc-800 pt-3 text-xs text-zinc-500">This video is currently managed as a single asset.</p>
      )}

      {editor && <AssetEditor
        editor={editor}
        parts={parts}
        sourceVideoOptions={sourceVideoOptions}
        onClose={() => setEditor(null)}
        onSave={(payload) => saveAsset(editor.kind, { id: editor.initial?.id, payload })}
      />}
    </section>
  );
}

function AssetRow({ title, subtitle, status, uploadId, assetId, playbackId, duration, createdAt, uploadState, uploadFilename, uploadSizeBytes, uploadError, posterUrl, thumbnailEndpoint, uploadControl, onEdit, onDelete, onDetach, extra, compact = false }) {
  const Icon = statusIcons[status] || Clock3;
  const muxState = assetId ? `Asset ${assetId}` : uploadId ? `Upload ${uploadId}` : playbackId ? `Playback ${playbackId}` : "No Mux asset linked";
  return (
    <div className={`flex min-w-0 items-center gap-3 rounded border border-zinc-800 bg-zinc-950/50 ${compact ? "px-3 py-2" : "px-3 py-2.5"}`}>
      <Icon size={15} className={`shrink-0 ${status === "ready" || status === "published" ? "text-emerald-400" : "text-zinc-500"}`} />
      <AssetPoster playbackId={playbackId} posterUrl={posterUrl} endpoint={thumbnailEndpoint} title={title} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="truncate text-sm font-medium text-white">{title}</span>
          <span className="text-[10px] uppercase text-zinc-500">{pretty(status)}</span>
          {duration != null && <span className="text-[10px] text-zinc-500">{Math.floor(duration / 60)}:{String(Math.floor(duration % 60)).padStart(2, "0")}</span>}
        </div>
        <div className="truncate text-[11px] text-zinc-500" title={subtitle || muxState}>{subtitle || muxState}</div>
        <div className="mt-0.5 flex flex-wrap gap-x-2 text-[10px] text-zinc-500">
          {uploadState && uploadState !== "idle" && <span>File {pretty(uploadState)}</span>}
          {uploadFilename && <span className="max-w-[180px] truncate" title={uploadFilename}>{uploadFilename}</span>}
          {uploadSizeBytes > 0 && <span>{formatUploadSize(uploadSizeBytes)}</span>}
          {createdAt && <time dateTime={createdAt}>{String(createdAt).slice(0, 10)}</time>}
          {playbackId ? <span className="text-emerald-400">Playback available</span> : <span>Playback unavailable</span>}
        </div>
        {uploadError && <div className="mt-0.5 truncate text-[10px] text-red-300" title={uploadError}>{uploadError}</div>}
        {extra}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {uploadControl}
        {onDetach && <button type="button" title="Detach linked video" onClick={onDetach} className="rounded p-1.5 text-pink-300 hover:bg-pink-950 hover:text-white"><Unlink size={14} /></button>}
        {onEdit && <button type="button" title="Edit asset" onClick={onEdit} className="rounded p-1.5 text-zinc-500 hover:bg-zinc-800 hover:text-white"><Pencil size={14} /></button>}
        {onDelete && <button type="button" title="Delete asset" onClick={onDelete} className="rounded p-1.5 text-zinc-500 hover:bg-red-950 hover:text-red-300"><Trash2 size={14} /></button>}
      </div>
    </div>
  );
}

function AssetPoster({ playbackId, posterUrl, endpoint, title }) {
  const [source, setSource] = useState(posterUrl || "");
  const [useProxy, setUseProxy] = useState(false);

  useEffect(() => {
    let alive = true;
    let objectUrl;
    if (posterUrl && !useProxy) {
      setSource(posterUrl);
      return () => { alive = false; };
    }
    if (!playbackId || !endpoint) {
      setSource("");
      return () => { alive = false; };
    }
    setSource("");
    api.get(endpoint, { params: { width: 160 }, responseType: "blob" }).then(({ data }) => {
      objectUrl = URL.createObjectURL(data);
      if (alive) setSource(objectUrl);
    }).catch(() => { if (alive) setSource(""); });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [posterUrl, playbackId, endpoint, useProxy]);

  return (
    <div className="relative aspect-video w-16 shrink-0 overflow-hidden rounded bg-zinc-900" aria-label={`${title} poster`}>
      {source ? (
        <img src={source} alt="" loading="lazy" className="absolute inset-0 h-full w-full object-cover" onError={() => {
          if (posterUrl && !useProxy) setUseProxy(true);
          else setSource("");
        }} />
      ) : <Film size={16} className="absolute inset-0 m-auto text-zinc-700" />}
    </div>
  );
}

function PlatformLinks({ matrix, kind, assetId, onLink }) {
  const linked = Object.entries(matrix).filter(([, entry]) => entry?.asset_ref?.asset_id === assetId);
  const available = Object.keys(matrix).filter(key => !matrix[key]?.asset_ref?.asset_id);
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {linked.map(([key, entry]) => (
        <button key={key} type="button" title="Unlink platform" onClick={() => onLink(key, null)} className="rounded bg-zinc-800 px-1.5 py-0.5 text-[10px] text-zinc-300 hover:bg-red-950">
          {key} · {pretty(entry.status || "not_published")} ×
        </button>
      ))}
      {available.length > 0 && (
        <select value="" aria-label={`Link ${kind} to platform`} onChange={event => event.target.value && onLink(event.target.value, assetId)} className="rounded border border-zinc-800 bg-zinc-950 px-1 py-0.5 text-[10px] text-zinc-400">
          <option value="">+ platform</option>
          {available.map(key => <option key={key} value={key}>{key}</option>)}
        </select>
      )}
    </div>
  );
}

function AssetEditor({ editor, parts, sourceVideoOptions, onClose, onSave }) {
  const initial = editor.initial || {};
  const [form, setForm] = useState(editor.kind === "part" ? {
    part_number: initial.part_number || Math.max(0, ...parts.map(part => part.part_number)) + 1,
    source_video_id: initial.source_video_id || "",
    label: initial.label || "", description: initial.description || "", status: initial.status || "awaiting_upload", mux_upload_id: initial.mux_upload_id || "",
    mux_asset_id: initial.mux_asset_id || "", playback_id: initial.playback_id || "", duration_seconds: initial.duration_seconds ?? "", notes: initial.notes || "",
  } : {
    source_video_id: initial.source_video_id || "",
    parent_type: initial.parent_type || "master", parent_id: initial.parent_id || "", cut_type: initial.cut_type || "trailer",
    label: initial.label || "", description: initial.description || "", target_platform: initial.target_platform || "x", access_type: initial.access_type || "free",
    price: initial.price ?? "", currency: initial.currency || "USD", platform_publish_status: initial.platform_publish_status || "not_published",
    mux_upload_id: initial.mux_upload_id || "", mux_asset_id: initial.mux_asset_id || "", playback_id: initial.playback_id || "",
    duration_seconds: initial.duration_seconds ?? "", notes: initial.notes || "",
  });
  const change = (key, value) => setForm(current => ({ ...current, [key]: value }));
  const submit = event => {
    event.preventDefault();
    const payload = { ...form };
    ["duration_seconds", "price", "part_number"].forEach(key => {
      if (key in payload) payload[key] = payload[key] === "" ? null : Number(payload[key]);
    });
    if (editor.kind === "cut" && !payload.parent_id) payload.parent_id = "";
    Object.keys(payload).forEach(key => { if (payload[key] === "") payload[key] = null; });
    onSave(payload);
  };
  const field = (label, key, type = "text") => <label className="block text-xs text-zinc-400">{label}<input type={type} value={form[key] ?? ""} onChange={event => change(key, event.target.value)} className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2.5 py-2 text-sm text-white" /></label>;
  const select = (label, key, options) => <label className="block text-xs text-zinc-400">{label}<select value={form[key]} onChange={event => change(key, event.target.value)} className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2.5 py-2 text-sm text-white">{options.map(option => <option key={option} value={option}>{pretty(option)}</option>)}</select></label>;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <form onSubmit={submit} className="max-h-[90vh] w-full max-w-xl space-y-3 overflow-y-auto rounded-md border border-zinc-700 bg-zinc-900 p-5">
        <div className="flex items-center justify-between"><h4 className="text-base font-semibold text-white">{initial.id ? "Edit" : "Add"} {editor.kind === "part" ? "Part" : "Cut"}</h4><button type="button" onClick={onClose} className="text-sm text-zinc-400 hover:text-white">Close</button></div>
        {editor.kind === "part" ? <>
          <div className="grid grid-cols-2 gap-3">{field("Part number", "part_number", "number")}{select("Status", "status", PART_STATUSES)}</div>
          {field("Title", "label")}
          <label className="block text-xs text-zinc-400">Description<textarea value={form.description} onChange={event => change("description", event.target.value)} rows={2} className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2.5 py-2 text-sm text-white" /></label>
          {sourceVideoOptions && <label className="block text-xs text-zinc-400">Attach existing video as this part<select value={form.source_video_id} onChange={event => change("source_video_id", event.target.value)} className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2.5 py-2 text-sm text-white"><option value="">No linked video</option>{sourceVideoOptions.map(option => <option key={option.id} value={option.id}>{option.title || option.id}</option>)}</select></label>}
        </> : <>
          <div className="grid grid-cols-2 gap-3">{select("Cut type", "cut_type", CUT_TYPES)}{select("Platform", "target_platform", PLATFORMS)}</div>
          {field("Title", "label")}
          <label className="block text-xs text-zinc-400">Description<textarea value={form.description} onChange={event => change("description", event.target.value)} rows={2} className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2.5 py-2 text-sm text-white" /></label>
          <div className="grid grid-cols-2 gap-3">{select("Access", "access_type", ACCESS_TYPES)}{select("Publish status", "platform_publish_status", ["not_published", "scheduled", "published", "archived"])}</div>
          {form.access_type === "ppv_paid_unlock" && <div className="grid grid-cols-2 gap-3">{field("Price", "price", "number")}{field("Currency", "currency")}</div>}
          <label className="block text-xs text-zinc-400">Parent asset<select value={`${form.parent_type}:${form.parent_id}`} onChange={event => { const [parent_type, ...id] = event.target.value.split(":"); setForm(current => ({ ...current, parent_type, parent_id: id.join(":") })); }} className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2.5 py-2 text-sm text-white"><option value={`master:${initial.parent_type === "master" ? initial.parent_id : ""}`}>Master video</option>{parts.map(part => <option key={part.id} value={`part:${part.id}`}>Part {part.part_number}{part.label ? ` · ${part.label}` : ""}</option>)}</select></label>
          {sourceVideoOptions && <label className="block text-xs text-zinc-400">Attach existing video as this cut<select value={form.source_video_id} onChange={event => change("source_video_id", event.target.value)} className="mt-1 w-full rounded border border-zinc-700 bg-zinc-950 px-2.5 py-2 text-sm text-white"><option value="">No linked video</option>{sourceVideoOptions.map(option => <option key={option.id} value={option.id}>{option.title || option.id}</option>)}</select></label>}
        </>}
        <div className="grid grid-cols-2 gap-3">{field("Mux upload ID", "mux_upload_id")}{field("Mux asset ID", "mux_asset_id")}</div>
        <div className="grid grid-cols-2 gap-3">{field("Playback ID", "playback_id")}{field("Duration (seconds)", "duration_seconds", "number")}</div>
        {field("Notes", "notes")}
        <div className="flex justify-end gap-2 pt-2"><button type="button" onClick={onClose} className="rounded border border-zinc-700 px-3 py-2 text-sm text-zinc-300">Cancel</button><button type="submit" className="rounded bg-cyan-700 px-3 py-2 text-sm font-medium text-white hover:bg-cyan-600">Save</button></div>
      </form>
    </div>
  );
}